import { MouseEvent, useState } from "react";
import styled from "styled-components";
import { css } from "@styled-system/css";
import { position, PositionProps, space, SpaceProps } from "styled-system";

import Box from "../Box/Box";
import Button from "../Button/Button";
import Link from "../Link/Link";
import Typography from "../Typography/Typography";
import Icon from "../Icon/Icon";
import Logo from "../Logo/Logo";
import LinkButton from "../Button/LinkButton";
import { processEventLabel } from "../Navigation/navItem.functions";

import { MobileMenuProps, NavMenuSection } from "./bcaHeader.type";
import MyBCAUser from "./MyBCAUser";
import { MenuLink } from "../Navigation/MenuLink.type";

const MobileMenuBackdrop = styled.div`
  position: fixed;
  height: 100vh;
  width: 100vw;
  top: 0;
  right: 0;
  overflow: none;
  z-index: 99;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
`;

const LinkLi = styled.li`
  line-height: 2.5;
  a {
    color: #3f403f;
    text-decoration: none;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  ${css({
    fontSize: "h6",
    fontFamily: "normal",
  })}
`;

const HeadingLink = styled(Link)<SpaceProps>`
  ${css({
    display: "flex",
    alignItems: "center",
    color: "primary-light-blue",
    fontWeight: "semiBold",
    lineHeight: 2.5,
  })}
  ${space}
`;

const CloseButton = styled.button<PositionProps>`
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  ${position}
`;

const Heading = styled(Typography)`
  ${css({
    color: "black",
    fontWeight: "semiBold",
    textTransform: "uppercase",
  })}
`;

const Divider = styled.hr<SpaceProps>`
  width: 100%;
  height: 2px;
  min-height: 2px;
  border: none;
  margin-bottom: 10px;
  ${css({
    backgroundColor: "divider-color",
  })}
  ${space}
`;

const MyBCAMenu = styled(Box)`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;

const renderFirstLevel = (
  navItems: MenuLink[],
  selectSubsection: (index: number) => void
) => {
  return (
    <div>
      <Heading variant="body">Main menu</Heading>
      <ul style={{ listStyle: "none", padding: "0", marginTop: "0" }}>
        {navItems.map((item, index) => {
          const eventLabel = processEventLabel(item.label);
          const eventAction = item.label === "Search" ? "search" : "navigate";

          return (
            <LinkLi
              key={item.id}
              onClick={() => {
                selectSubsection(index);
              }}
            >
              <a
                href={item.link}
                data-en="click"
                data-ec="button"
                data-el={eventLabel}
                data-ea={eventAction}
                data-es="header"
              >
                {item.label}
              </a>
              {item.children || item.sections ? (
                <Icon
                  name="angle-right"
                  weight="regular"
                  color="#3f403f"
                  width="14px"
                  height="20px"
                ></Icon>
              ) : null}
            </LinkLi>
          );
        })}
      </ul>
    </div>
  );
};

const renderSubSections = (section: MenuLink) => {
  const eventAction = section.label === "Search" ? "search" : "navigate";
  const eventSection = processEventLabel(section.label);

  const renderSection = (subSection: NavMenuSection, index: number) => {
    return (
      <div key={subSection.id}>
        {index > 0 && <Divider />}
        <ul style={{ listStyle: "none", padding: "0", marginTop: "0" }}>
          {subSection.children.map((item, index) => {
            const eventLabel = processEventLabel(item.label);

            if (index === 0) {
              return (
                <Heading key={item.id} variant="body">
                  {item.label}
                </Heading>
              );
            }
            return (
              <LinkLi key={item.id}>
                <a
                  href={item.link}
                  data-en="click"
                  data-ec="button"
                  data-el={eventLabel}
                  data-ea={eventAction}
                  data-es={eventSection}
                >
                  {item.label}
                </a>
                {item.children || item.sections ? (
                  <Icon
                    name="angle-right"
                    weight="regular"
                    color="#3f403f"
                    width="14px"
                    height="20px"
                  ></Icon>
                ) : null}
              </LinkLi>
            );
          })}
        </ul>
      </div>
    );
  };
  if (section.sections) {
    return section.sections.map((subSection, index) =>
      renderSection(subSection, index)
    );
  }
  return (
    <ul style={{ listStyle: "none", padding: "0", marginTop: "0" }}>
      <Heading variant="body">{section.label}</Heading>
      {section.children &&
        section.children.map((item: MenuLink) => {
          const eventLabel = processEventLabel(item.label);

          return (
            <LinkLi key={item.id}>
              <a
                href={item.link}
                data-en="click"
                data-ec="button"
                data-el={eventLabel}
                data-ea={eventAction}
                data-es={eventSection}
              >
                {item.label}
              </a>
            </LinkLi>
          );
        })}
    </ul>
  );
};

const MobileMenu = ({
  isAuthenticated,
  isMyBCAMenu,
  navItems,
  myBCALinks,
  urls,
  user,
  onClose,
}: MobileMenuProps) => {
  const [selectedSectionIndex, setSelectionIndex] = useState<number | null>(
    null
  );

  const selectSubsection = (index: number) => {
    if (navItems[index].sections || navItems[index].children) {
      setSelectionIndex(index);
    }
  };

  const { selling, buying, accountLink } = myBCALinks;

  const sellingLinks = selling?.links.filter((link) => link.isVisible);
  const buyingLinks = buying?.links.filter((link) => link.isVisible);

  const navMenu =
    selectedSectionIndex !== null
      ? renderSubSections(navItems[selectedSectionIndex])
      : renderFirstLevel(navItems, selectSubsection);

  return (
    <MobileMenuBackdrop onClick={onClose}>
      <div
        style={{
          background: "white",
          width: "325px",
          height: "100%",
          marginLeft: "auto",
          padding: "16px 20px",
          boxSizing: "border-box",
          position: "relative",
        }}
        onClick={(e: MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
        }}
      >
        {selectedSectionIndex !== null ? (
          <Button
            text="Back"
            variant="tertiary"
            icon="arrow-right-solid"
            iconAlignment="left"
            css={`
              padding: 0;
              margin-left: -4px;
              margin-top: -8px;
              svg {
                transform: rotate(180deg);
              }
              cursor: pointer;
            `}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setSelectionIndex(null);
            }}
          />
        ) : (
          <Logo ml="-4px" role="logo" />
        )}

        <CloseButton
          position="absolute"
          right="27px"
          top="20px"
          onClick={onClose}
        >
          <Icon name="times" weight="light" width="16px" color="#3f403f" />
        </CloseButton>
        <nav
          style={{
            height: window.innerHeight - 100,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!isMyBCAMenu && (
            <>
              {navMenu}
              <Divider />
            </>
          )}

          {isAuthenticated && selectedSectionIndex === null && (
            <MyBCAMenu>
              <div>
                <MyBCAUser user={user} accountLink={accountLink} mt={3} />
                {buying && (
                  <ul
                    style={{
                      listStyle: "none",
                      padding: "0",
                      margin: "0",
                      flex: 1,
                    }}
                  >
                    <HeadingLink
                      key={buying.id}
                      href={buying.link}
                      mb={isMyBCAMenu ? 2 : 0}
                    >
                      {buying.label}
                      <Icon name="arrow-right" height={14} ml={3} />
                    </HeadingLink>
                    {isMyBCAMenu &&
                      buyingLinks?.map((link) => (
                        <LinkLi key={link.id}>
                          <a href={link.link}>{link.label}</a>
                        </LinkLi>
                      ))}
                  </ul>
                )}
                {isMyBCAMenu && selling && <Divider mt={4} />}
                {selling && (
                  <ul
                    style={{
                      listStyle: "none",
                      padding: "0",
                      margin: "0",
                      flex: 1,
                    }}
                  >
                    <HeadingLink
                      key={selling.id}
                      href={selling.link}
                      mb={isMyBCAMenu ? 2 : 0}
                    >
                      {selling.label}
                      <Icon name="arrow-right" height={14} ml={3} />
                    </HeadingLink>
                    {isMyBCAMenu &&
                      sellingLinks &&
                      sellingLinks.map((link) => (
                        <LinkLi key={link.id}>
                          <a href={link.link}>{link.label}</a>
                        </LinkLi>
                      ))}
                  </ul>
                )}
              </div>
              <Divider mt={4}></Divider>
              <ul
                style={{
                  listStyle: "none",
                  padding: "0",
                  marginTop: "0",
                  flex: 1,
                }}
              >
                <LinkLi
                  style={{
                    justifyContent: "flex-start",
                  }}
                >
                  <Icon
                    name="sign-out"
                    weight="regular"
                    color="black"
                    width="16px"
                    mr={2}
                  />
                  <a href={urls.logout} target="_top">
                    Sign Out
                  </a>
                </LinkLi>
              </ul>
            </MyBCAMenu>
          )}
          {!isAuthenticated && selectedSectionIndex === null && (
            <MyBCAMenu>
              <Heading variant="body">MyBCA</Heading>
              <Box>
                <LinkButton
                  width="170px"
                  link={urls.login}
                  shouldOpenInNewWindow={false}
                >
                  Login
                </LinkButton>
                <LinkButton
                  css={`
                    padding-left: 30px;
                    padding-right: 0;
                  `}
                  variant="tertiary"
                  link="https://www.bca.co.uk/buy/Join-BCA/Register/"
                >
                  Register
                </LinkButton>
              </Box>
            </MyBCAMenu>
          )}
        </nav>
      </div>
    </MobileMenuBackdrop>
  );
};

export default MobileMenu;
